import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar.tsx';
import Footer from './components/Footer.tsx';
import Home from './pages/Home.tsx';
import About from './pages/About.tsx';
import ServiceDetail from './pages/ServiceDetail.tsx';
import HowItWorks from './pages/HowItWorks.tsx';
import Contact from './pages/Contact.tsx';
import Booking from './pages/Booking.tsx';
import Resources from './pages/Resources.tsx';
import ServicesOverview from './components/Services.tsx';
import AdminLogin from './pages/AdminLogin.tsx';
import AdminDashboard from './pages/AdminDashboard.tsx';
import AdminRegistrations from './pages/AdminRegistrations.tsx';
import AdminSidebar from './components/AdminSidebar.tsx';
import { Page } from './types.ts';

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('home');

  // Dynamic Metadata & Scroll Management
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const metaDescriptions: Record<string, string> = {
      home: "FinTrack is Nigeria's #1 platform for 2026 business compliance. Professional CAC registration, tax management, and AI bookkeeping.",
      about: "Meet the chartered accountants and legal experts behind FinTrack. Simplifying business for high-growth Nigerian founders.",
      'cac-registration': "Register your Limited Liability Company or Business Name with CAC in 14 days. Includes TIN, Status Report, and 2026 BOR compliance.",
      'tax-compliance': "Expert FIRS & LIRS tax management. VAT, CIT, and PAYE filings automated via our premium compliance engine.",
      bookkeeping: "Professional outsourced accounting for Nigerian startups. Get investor-ready financial reports by the 5th of every month.",
      'post-inc': "Update your CAC records, increase share capital, or file annual returns to maintain 'Active' regulatory status.",
      advisory: "Strategic financial consulting and regulatory moat strategy for startups scaling in the 2026 African tech ecosystem.",
      'how-it-works': "A 4-step seamless process to bring your business into full legal and fiscal compliance in Nigeria.",
      resources: "Knowledge hub for the 2026 Nigerian economy. Insights on the Finance Act, Startup Act, and digital tax reform.",
      contact: "Get in touch with a FinTrack compliance expert today for a personalized audit of your business records.",
      booking: "Schedule a direct strategy session with our senior compliance partners to launch or scale your venture.",
    };

    const titles: Record<string, string> = {
      home: "FinTrack | Premium Startup Compliance Nigeria 2025/2026",
      about: "About FinTrack | Nigeria's Leading Compliance Partners",
      'cac-registration': "CAC Registration Services | Fast-Track Incorporation Nigeria",
      'tax-compliance': "FIRS Tax Compliance & Filing | Nigerian Startups 2026",
      bookkeeping: "Professional Bookkeeping & Accounting for Startups | FinTrack",
      'post-inc': "Post-Incorporation & Annual Returns | CAC Nigeria",
      advisory: "Business Advisory & Growth Strategy | FinTrack Compliance",
      'how-it-works': "Process & Workflow | How FinTrack Simplifies Compliance",
      resources: "Resources & 2026 Fiscal Insights | FinTrack Knowledge Hub",
      contact: "Contact Us | Speak to a Compliance Expert",
      booking: "Book Strategy Session | FinTrack Compliance",
    };

    if (titles[currentPage]) {
      document.title = titles[currentPage];
    } else if (currentPage.startsWith('admin-')) {
      document.title = "Admin Console | FinTrack Operations";
    }

    const metaDescriptionTag = document.querySelector('meta[name="description"]');
    if (metaDescriptionTag && metaDescriptions[currentPage]) {
      metaDescriptionTag.setAttribute('content', metaDescriptions[currentPage]);
    }
  }, [currentPage]);

  const renderPage = () => {
    switch (currentPage) {
      case 'home': return <Home setPage={setCurrentPage} />;
      case 'about': return <About />;
      case 'cac-registration': return <ServiceDetail type="cac" setPage={setCurrentPage} />;
      case 'tax-compliance': return <ServiceDetail type="tax" setPage={setCurrentPage} />;
      case 'bookkeeping': return <ServiceDetail type="bookkeeping" setPage={setCurrentPage} />;
      case 'post-inc': return <ServiceDetail type="post-inc" setPage={setCurrentPage} />;
      case 'advisory': return <ServiceDetail type="advisory" setPage={setCurrentPage} />;
      case 'how-it-works': return <HowItWorks setPage={setCurrentPage} />;
      case 'contact': return <Contact />;
      case 'booking': return <Booking setPage={setCurrentPage} />;
      case 'resources': return <Resources setPage={setCurrentPage} />;
      case 'admin-login': return <AdminLogin setPage={setCurrentPage} />;
      case 'admin-dashboard':
      case 'admin-registrations':
      case 'admin-settings':
        return (
          <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
            <AdminSidebar currentPage={currentPage} setPage={setCurrentPage} />
            <div className="flex-1 ml-64">
              {currentPage === 'admin-dashboard' && <AdminDashboard />}
              {currentPage === 'admin-registrations' && <AdminRegistrations />}
              {currentPage === 'admin-settings' && (
                <div className="p-12">
                  <h1 className="text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">Portal Settings</h1>
                  <p className="text-slate-500">Internal system configurations are currently managed by the technical lead.</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'services': return (
        <div className="pt-32 pb-24 animate-fade-in">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-20">
              <h1 className="text-5xl font-display font-bold text-slate-900 dark:text-white mb-6">Our Services</h1>
              <p className="text-xl text-slate-600 dark:text-slate-400">Everything your startup needs to stay compliant, from launch to scale.</p>
            </div>
            <ServicesOverview setPage={setCurrentPage} />
          </div>
        </div>
      );
      default: return <Home setPage={setCurrentPage} />;
    }
  };

  const isAdminPage = currentPage.startsWith('admin-');

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-300">
      {!isAdminPage && <Navbar currentPage={currentPage} setPage={setCurrentPage} />}
      
      <main className={!isAdminPage ? "animate-reveal duration-1000" : ""}>
        {renderPage()}

        {currentPage === 'home' && (
          <section className="relative py-24 sm:py-28 border-y border-slate-100 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/10 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-grid opacity-10 pointer-events-none"></div>
            <div className="max-w-7xl mx-auto px-4 relative text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-md shadow-sm text-primary dark:text-accent-400 text-[9px] font-black uppercase tracking-[0.4em] mb-6 border border-slate-200 dark:border-slate-700">
                <span className="material-symbols-rounded text-[14px] fill-1">verified_user</span>
                Official Regulatory Accreditation
              </div>
              <h3 className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white leading-tight">
                Trusted by <span className="text-primary italic">Regulators.</span>
              </h3>
            </div>
          </section>
        )}
      </main>

      {!isAdminPage && <Footer setPage={setCurrentPage} />}
    </div>
  );
};

export default App;